import React, { useEffect, useRef } from 'react';

export interface ItemActionMenuProps {
  itemPath: string;
  itemType: 'flow' | 'phase' | 'step' | 'block';
  onClose: () => void;
  onAddItem: (parentPath: string, itemTypeToAdd: 'phase' | 'step' | 'block') => void;
  onDeleteItem: (itemPath: string) => void;
  onChangeItemType: (itemPath: string, newType: 'phase' | 'step') => void;
  onCreateStepFromSelection: (selection: string, blockPath: string) => void;
}

const ItemActionMenu: React.FC<ItemActionMenuProps> = ({
  itemPath,
  itemType,
  onClose,
  onAddItem,
  onDeleteItem,
  onChangeItemType,
  onCreateStepFromSelection,
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const handleAction = (action: () => void) => {
    action();
    onClose();
  };

  const selection = typeof window !== 'undefined' ? window.getSelection()?.toString().trim() : '';

  return (
    <div
      ref={dropdownRef}
      className="absolute right-0 top-full mt-1 w-56 bg-white rounded-md shadow-lg z-20 border border-gray-200 transform translate-y-1"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="py-1 text-sm">
        {itemType === 'block' && selection && (
          <>
            <button 
              onClick={() => handleAction(() => onCreateStepFromSelection(selection, itemPath))} 
              className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100 hover:text-gray-900"
            >
              Create Step from Selection
            </button>
            <div className="border-t border-gray-100 my-1"></div>
          </>
        )}

        {/* Add Actions */}
        {itemType === 'flow' && (
          <button 
            onClick={() => handleAction(() => onAddItem(itemPath, 'phase'))} 
            className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100 hover:text-gray-900"
          >
            Add Phase Below
          </button>
        )}
        {(itemType === 'phase' || itemType === 'step') && (
          <button 
            onClick={() => handleAction(() => onAddItem(itemPath, 'step'))} 
            className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100 hover:text-gray-900"
          >
            Add Step Below
          </button>
        )}
        {/* itemType === 'step' can also add a block, but that's often an implicit action or part of step creation/editing */}

        {/* Change Type Actions */}
        {(itemType === 'step' || itemType === 'phase' || itemType === 'block') && (
            <div className="border-t border-gray-100 my-1"></div>
        )}
        {itemType === 'step' && (
          <button 
            onClick={() => handleAction(() => onChangeItemType(itemPath, 'phase'))} 
            className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100 hover:text-gray-900"
          >
            Change to Phase
          </button>
        )}
        {itemType === 'phase' && (
          <button 
            onClick={() => handleAction(() => onChangeItemType(itemPath, 'step'))} 
            className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100 hover:text-gray-900"
          >
            Change to Step
          </button>
        )}
         {itemType === 'block' && (
          <button 
            onClick={() => handleAction(() => onChangeItemType(itemPath, 'step'))} 
            className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100 hover:text-gray-900"
          >
            Convert Block to New Step
          </button>
        )}

        {/* Delete Action - not available for Flow root */}
        {itemType !== 'flow' && (
          <>
            <div className="border-t border-gray-100 my-1"></div>
            <button 
              onClick={() => handleAction(() => onDeleteItem(itemPath))} 
              className="block w-full text-left px-4 py-2 text-red-600 hover:bg-red-50 hover:text-red-700"
            >
              Delete {itemType.charAt(0).toUpperCase() + itemType.slice(1)}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default ItemActionMenu; 